// @flow
import { getLuminance, parseToRgb } from "polished";

import { getThemeStringProp, getProp } from "./themeProps";

import { ThemePropertyException } from "../../exceptions";

import type { Theme, UtilThemeProps } from "../types";

/**
 * calculate the contrast ratio of two colors
 */
const getColorContrast = (color1: string, color2: string): number => {
  const luminance1 = getLuminance(color1);
  const luminance2 = getLuminance(color2);
  return parseFloat(
    (luminance1 > luminance2
      ? (luminance1 + 0.05) / (luminance2 + 0.05)
      : (luminance2 + 0.05) / (luminance1 + 0.05)
    ).toFixed(2),
  );
};

/*
 * The YIQ equation converts the RGB color (0 to 255) into a YIQ color space.
 * YIQ is the standard formula for calculating the perceived brightness of a color,
 * and is recommended by the World Wide Web Consortium (W3C).
 */
/**
 */
const getYIQ = (color: string): number => {
  const rgb = parseToRgb(color);

  return (rgb.red * 299 + rgb.green * 587 + rgb.blue * 114) / 1000;
};

/**
 * Get dark or light color, depending on the given color
 * When the contrast between the color and the light or dark color is too low,
 * the preferred color is returned
 */
const getContrastYIQ = (
  color: string,
  dark: string = "#212529",
  light: string = "#fff",
  preferredColor?: string,
): string => {
  const yiqColor = getYIQ(color);

  if (preferredColor && getColorContrast(color, preferredColor) >= 4.5) {
    return preferredColor;
  }

  const darkColor = getYIQ(dark) < 127.5 ? dark : "#212529";
  const lightColor = getYIQ(light) > 127.5 ? light : "#fff";

  if (yiqColor > 127.5) {
    if (getColorContrast(color, darkColor) < 3) {
      return "#212529";
    }

    return darkColor;
  }

  if (getColorContrast(color, lightColor) < 3) {
    return "#fff";
  }
  return lightColor;
};

/**
 */
const getBackgroundColor = (
  theme: Theme,
  bgColorProperty: string,
  defaultValue: string,
) => {
  let bgColor = getProp(theme, bgColorProperty, defaultValue);

  if (typeof bgColor === "object") {
    bgColor = bgColor?.backgroundColor ?? defaultValue;
  }

  if (typeof bgColor === "string") {
    return bgColor;
  }

  throw new ThemePropertyException(
    `${bgColorProperty} is not a string, but: ${typeof bgColor}`,
  );
};

/**
 * Retrieve the contrast color for the given background property,
 * based on theme properties
 */
const renderContrastColor =
  (
    bgColorProperty: string,
    preferredColorProperty?: string,
    standardDarkProperty: string = "YIQ_DARK_COLOR",
    standardLightProperty: string = "YIQ_LIGHT_COLOR",
  ): ((UtilThemeProps) => string) =>
  ({ theme = { settings: {} } }: UtilThemeProps) => {
    const bgColor = getBackgroundColor(theme, bgColorProperty, "#ffffff");
    const preferredColor = getThemeStringProp(theme, preferredColorProperty);
    const darkColor = getThemeStringProp(
      theme,
      standardDarkProperty,
      "#212529",
    );
    const lightColor = getThemeStringProp(theme, standardLightProperty, "#fff");

    return getContrastYIQ(bgColor, darkColor, lightColor, preferredColor);
  };

export { getColorContrast, getYIQ, getContrastYIQ, renderContrastColor };
